/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Badge, Card, Pagination } from '@nexigendigital/nxui'
import { billingAPI } from 'api/billing'
import { setInvoice } from 'store/slices/billingSlice'
import { Invoice } from 'models/billing'
import { Meta } from 'models/app'
import { useIsMounted } from 'helpers/hooks'
import { aud, getInvoiceStatusColour, parseISOToLocalString } from 'helpers/utils'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function InvoicesContainer() {
    const dispatch = useDispatch()
    const isMounted = useIsMounted()
    const [meta, setMeta] = useState<Meta | undefined>(undefined)
    const { isLoading, data } = billingAPI.endpoints.invoices.useQuery(meta, { skip: !meta })

    const handleMeta = useCallback((meta) => {
        setMeta(meta)
    }, [])

    useEffect(() => {
        setMeta({
            page: 1,
            sort_by: undefined,
            filter_by: undefined,
            search_by: undefined
        })
    }, [])

    const columns = useMemo(() => [
        {
            Header: 'Invoice #',
            accessor: 'id'
        },
        {
            Header: 'Status',
            accessor: 'status'
        },
        {
            Header: 'Invoice Total',
            accessor: 'total'
        },
        {
            Header: 'Amount Due',
            accessor: 'amount_due'
        },
        {
            Header: 'Due Date',
            accessor: 'due_date'
        },
        {
            Header: 'Date Paid',
            accessor: 'date_paid'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ], [])
    const invoices = useMemo(() => {
        if (data) {
            return data.data.map(({ id, status, total, amount_due, due_date, date_paid }: Invoice) => {
                return {
                    id: (
                        <Anchor color={'primary'} onClick={() => dispatch(setInvoice(id))}>
                            {id}
                        </Anchor>
                    ),
                    status: <Badge color={getInvoiceStatusColour(status)}>{status.toUpperCase()}</Badge>,
                    total: <BlackText>{aud(total)}</BlackText>,
                    amount_due: <BlackText>{aud(amount_due)}</BlackText>,
                    due_date: parseISOToLocalString(due_date),
                    date_paid: date_paid === null ? '' : parseISOToLocalString(date_paid),
                    action: (
                        <Anchor color={'primary'} onClick={() => dispatch(setInvoice(id))}>
                            {status === 'paid' || status === 'cancelled' ? 'View' : 'Pay'}
                        </Anchor>
                    )
                }
            })
        }

        return []
    }, [data])

    const handleSort = useCallback((sortBy) => {
        if (isMounted() && sortBy.length > 0) {
            const { id, desc } = sortBy[0]
            setMeta((prevState) => {
                if (prevState) {
                    return {
                        ...prevState,
                        sort_by: `${desc ? '-' : ''}${id}`
                    }
                }

                return prevState
            })
        }
    }, [])

    return (
        <Invoices.Card
            table={{
                conditions: {
                    loading: isLoading && 'Loading Invoices...',
                    search: false,
                    pagination: false
                },
                render: {
                    columns: () => columns,
                    data: () => invoices
                },
                onSort: handleSort
            }}
            footer={(
                <Pagination
                    currentPage={data?.meta.current_page || 1}
                    totalCount={data?.meta.total || 1}
                    pageSize={data?.meta.per_page || 15}
                    onPageChange={(page: number) => handleMeta({ ...meta, page })}
                />
            )}
        />
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Invoices = {
    Card: styled(Card)`
        width: 100%;
        margin-bottom: 0;
        border-radius: 0 0 16px 16px;

        > header {
            border-radius: 0;
        }
    `
}
const BlackText = styled.span`
    color: ${props => props.theme['black--100']};
`

export { InvoicesContainer as Invoices }
